import { defineStore } from 'pinia';
import { ref } from 'vue';
import usersAPI from '@/api/users';
import type { User } from '@/types/User';
import { useAlertStore } from '@/store/alertStore';

export const useUsersStore = defineStore('users', () => {
  const users = ref<User[]>([]);
  const users_result = ref<User[]>([]);
  const assigned_records = ref<any[]>([]);
  
  const fetchUsers = async (params: any): Promise<{ data: User[]; total: number }> => {
    try {
      
      const response = await usersAPI.getUsers(params);
      const data: User[] = response.data.data;
      const total = response.data.meta.total;
      users.value = data;
      return { data, total };
    } catch (error) {
      console.error('Error fetching user s:', error);
      throw error;
    }
  };

  const fetchUserById = async (id: number) => {
    try {
      const response = await usersAPI.getUserById(id);
      return response.data.data as User;
    } catch (error) {
      console.error(`Error fetching user  with ID ${id}:`, error);
      throw error;
    }
  };

  const createUser = async (userData: Partial<User>) => {
    try {
      const response = await usersAPI.createUser(userData);
      if (response.data?.success === false) {
        throw response.data;
      }
      users.value.push((response.data.data ?? response.data) as User);
      return response.data;
    } catch (error) {
      console.error('Error creating user :', error);
      throw error;
    }
  };

  const updateUser = async (id: number, userData: Partial<User>) => {
    try {
      const response = await usersAPI.updateUser(id, userData);
      if (response.data?.success === false) {
        throw response.data;
      }
      const updated = (response.data.data ?? response.data) as User;
      const index = users.value.findIndex((c) => c.id === id);
      if (index !== -1) {
        users.value[index] = updated;
      }
      return response.data;
    } catch (error) {
      console.error(`Error updating user  with ID ${id}:`, error);
      throw error;
    }
  };

  const updatePassword = async (id: number, password: string) => {
    try {
      const response = await usersAPI.updatePassword(id, password);
      if (!response.data.success) {
        throw response.data;
      }
      alertStore.showAlert('Contraseña modificada con éxito', 'success');
    } catch (error) {
      console.error(`Error updating user  with ID ${id}:`, error);
      alertStore.showAlert(
        'Ocurrió un error al modificar contraseña.',
        'error'
      );
      throw error;
    }
  };

  const deleteUser = async (id: number) => {
    try {
      await usersAPI.deleteUser(id);
      users.value = users.value.filter((c) => c.id !== id);
    } catch (error) {
      console.error(`Error deleting user  with ID ${id}:`, error);
      throw error;
    }
  };

  const searchUsers = async (params: any): Promise<{ data: User[]}> => {
    try {
      const response = await usersAPI.getUsers(params);
      const data: User[] = response.data.data;
      users_result.value = data
      return { data };
    } catch (error) {
      console.error('Error fetching user s:', error);
      throw error;
    }
  };

  const fetchAssignedRecords = async (id: number) => {
    try {
      const response = await usersAPI.getAssignedRecords(id);
      const data = response.data.data;
      assigned_records.value = data
      return data;
    } catch (error) {
      console.error('Error fetching user s:', error);
      throw error;
    }
  };

  const alertStore = useAlertStore();

  const reassignRecords = async (data: Partial<User>) => {
    try {
      const response = await usersAPI.reassignRecords(data);
      if (!response.data.success) {
        throw response.data;
      }
      alertStore.showAlert('Registros reasignados con éxito', 'success');
      return response.data;
    } catch (error:any) {
      console.error('Error creating user :', error);
      alertStore.showAlert(
        error?.response?.data?.message || 'Ocurrió un error al reasignar los registros.',
        'error'
      );
      throw error;
    }
  };

  return {
    users,
    users_result,
    fetchUsers,
    fetchUserById,
    createUser,
    updateUser,
    deleteUser,
    searchUsers,
    fetchAssignedRecords,
    reassignRecords,
    updatePassword
  };
});
